import { useEffect, useState } from "react"

import {
  Button,
  Card,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@coval/ui"
import { ChevronDown, MessageSquare } from "lucide-react"

import type { ReportConversationTurn } from "../../hooks/useReportConversation"
import { formatDate } from "../../utils/reportClient"
import { reportClass } from "./ReportStyles"

const OUTCOMES = {
  pending: "Working…",
  applied: "Changes saved",
  unchanged: "No changes",
  failed: "Request failed",
  partial: "Some changes saved",
}

export function ReportConversation({
  turns,
  busy,
  onShowChanges,
}: {
  turns: ReportConversationTurn[]
  busy: string
  onShowChanges?: (turn: ReportConversationTurn) => void
}) {
  const [expanded, setExpanded] = useState(true)
  const latest = turns.at(-1)
  useEffect(() => {
    if (latest?.status === "pending") {
      setExpanded(true)
    }
  }, [latest?.id, latest?.status])

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded} asChild>
      <Card className={reportClass("conversation-panel")}>
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            className={reportClass("conversation-toggle")}
          >
            <MessageSquare />
            <span>Notebook chat ({turns.length})</span>
            <ChevronDown />
          </Button>
        </CollapsibleTrigger>
        {!expanded && latest && (
          <div className={reportClass("conversation-preview")}>
            <p>
              <strong>You:</strong> {latest.user_message}
            </p>
            <p>
              <strong>{OUTCOMES[latest.status]}:</strong>{" "}
              {latest.assistant_message}
            </p>
          </div>
        )}
        <CollapsibleContent>
          {turns.length === 0 ? (
            <p className={reportClass("conversation-empty")}>
              Your requests, Sofia’s replies, and the resulting revisions appear
              here.
            </p>
          ) : (
            <ol className={reportClass("conversation-list")}>
              {turns.map((turn) => (
                <li key={turn.id} className={reportClass("conversation-turn")}>
                  <div className={reportClass("conversation-meta")}>
                    <strong>You</strong>
                    <time dateTime={turn.created_at}>
                      {formatDate(turn.created_at, true)}
                    </time>
                  </div>
                  <p className={reportClass("conversation-message")}>
                    {turn.user_message}
                  </p>
                  <div
                    className={reportClass("conversation-reply")}
                    aria-live={turn.id === latest?.id ? "polite" : "off"}
                  >
                    <div className={reportClass("conversation-meta")}>
                      <strong>Sofia</strong>
                      <span>{OUTCOMES[turn.status]}</span>
                    </div>
                    <p className={reportClass("conversation-message")}>
                      {turn.assistant_message}
                    </p>
                    {turn.revision_after !== undefined &&
                      turn.revision_after > turn.revision_before &&
                      onShowChanges && (
                        <Button
                          variant="link"
                          disabled={Boolean(busy)}
                          onClick={() => onShowChanges(turn)}
                        >
                          Show changes
                        </Button>
                      )}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}
